import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function SecurityPage() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Quali5d Security — Enterprise-Grade Data Protection</title>
        <meta name="description" content="SSL Labs A rating, TLS 1.3, AES-256 encryption at rest, SOC 2 Type II infrastructure, OWASP Top 10 compliant. How Quali5d protects your recruitment data." />
        <link rel="canonical" href="https://www.quali5d.com/security" />
        <meta property="og:title" content="Quali5d Security — Enterprise-Grade Data Protection" />
        <meta property="og:description" content="SSL Labs A rating, TLS 1.3, AES-256 encryption at rest, SOC 2 Type II infrastructure, OWASP Top 10 compliant. How Quali5d protects your recruitment data." />
        <meta property="og:url" content="https://www.quali5d.com/security" />
        <meta name="twitter:title" content="Quali5d Security — Enterprise-Grade Data Protection" />
        <meta name="twitter:description" content="SSL Labs A rating, TLS 1.3, AES-256 encryption at rest, SOC 2 Type II infrastructure, OWASP Top 10 compliant. How Quali5d protects your recruitment data." />
      </Helmet>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            <span className="text-sm font-medium">Back to Quali5d</span>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-xs">Q5</span>
            </div>
            <span className="font-bold text-gray-900 text-sm">Quali5d</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center gap-2 bg-gray-900 rounded-full px-4 py-1.5 mb-5">
            <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
            </svg>
            <span className="text-xs font-semibold text-green-400">Security Overview</span>
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-4">Security at Quali5d</h1>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            We take the security of your recruitment data seriously. Here's how we protect it.
          </p>
        </div>

        {/* SSL Rating — Hero Card */}
        <div className="bg-gray-900 rounded-2xl p-8 mb-8 flex flex-col md:flex-row items-center gap-6">
          <div className="flex-shrink-0">
            <a href="https://www.ssllabs.com/ssltest/analyze.html?d=www.quali5d.com"
              target="_blank" rel="noopener noreferrer"
              className="block w-24 h-24 bg-green-500 rounded-2xl flex items-center justify-center hover:bg-green-400 transition-colors">
              <span className="text-white text-5xl font-black">A</span>
            </a>
          </div>
          <div className="text-center md:text-left">
            <div className="text-white text-xl font-bold mb-1">SSL Labs Rating: A</div>
            <div className="text-gray-400 text-sm mb-3">Assessed by Qualys SSL Labs — May 2026</div>
            <div className="flex flex-wrap gap-2 justify-center md:justify-start">
              {['TLS 1.3', 'TLS 1.2', 'Forward Secrecy: Robust', 'HSTS Enabled', 'OCSP Stapling'].map(tag => (
                <span key={tag} className="bg-green-500/20 text-green-400 text-xs font-semibold px-2.5 py-1 rounded-full">{tag}</span>
              ))}
            </div>
          </div>
          <div className="md:ml-auto">
            <a href="https://www.ssllabs.com/ssltest/analyze.html?d=www.quali5d.com"
              target="_blank" rel="noopener noreferrer"
              className="text-xs text-gray-400 hover:text-white transition-colors underline">
              View full report →
            </a>
          </div>
        </div>

        {/* 3-column security cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">

          {/* Infrastructure */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
              </svg>
            </div>
            <h3 className="font-bold text-gray-900 mb-3">Infrastructure</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>Hosted on Microsoft Azure</li>
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>SOC 2 Type II certified data centres</li>
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>Azure App Service isolated environment</li>
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>PostgreSQL with encrypted connections</li>
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>Azure Blob Storage with private containers</li>
            </ul>
          </div>

          {/* Encryption */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
              </svg>
            </div>
            <h3 className="font-bold text-gray-900 mb-3">Encryption</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>TLS 1.3 for all data in transit</li>
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>AES-256 encryption at rest</li>
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>Forward Secrecy: Robust</li>
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>Passwords hashed with bcrypt</li>
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>JWT tokens with expiry</li>
            </ul>
          </div>

          {/* Application */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 1.944A11.954 11.954 0 0117.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z" clipRule="evenodd"/>
              </svg>
            </div>
            <h3 className="font-bold text-gray-900 mb-3">Application Security</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>OWASP ZAP scanned — no critical findings</li>
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>Security headers (CSP, HSTS, X-Frame)</li>
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>Role-based access control (RBAC)</li>
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>API docs disabled in production</li>
              <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">✓</span>No sensitive data in logs</li>
            </ul>
          </div>
        </div>

        {/* Vulnerability scan results */}
        <div className="bg-white rounded-2xl border border-gray-200 p-8 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Vulnerability Scan Results</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { name: 'POODLE (SSLv3)', result: 'Not vulnerable' },
              { name: 'POODLE (TLS)', result: 'Not vulnerable' },
              { name: 'Heartbleed', result: 'Not vulnerable' },
              { name: 'BEAST attack', result: 'Mitigated server-side' },
              { name: 'ROBOT vulnerability', result: 'Not vulnerable' },
              { name: 'Ticketbleed', result: 'Not vulnerable' },
              { name: 'OpenSSL CVE-2014-0224', result: 'Not vulnerable' },
              { name: 'OpenSSL Padding Oracle', result: 'Not vulnerable' },
              { name: 'GOLDENDOODLE', result: 'Not vulnerable' },
              { name: 'Zombie POODLE', result: 'Not vulnerable' },
              { name: 'OWASP Top 10', result: 'No high/critical findings' },
              { name: 'SSL/TLS compression', result: 'Disabled' },
            ].map(item => (
              <div key={item.name} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                <span className="text-sm text-gray-700">{item.name}</span>
                <span className="text-xs font-semibold text-green-600 bg-green-50 px-2.5 py-1 rounded-full">{item.result}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Data retention */}
        <div className="bg-white rounded-2xl border border-gray-200 p-8 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Data Retention & Deletion</h2>
          <div className="space-y-4">
            {[
              { type: 'Audio & Video Recordings', period: '90 days', note: 'Permanently deleted after 90 days' },
              { type: 'Transcripts & AI Documents', period: 'Subscription + 30 days', note: 'Deleted within 30 days of account closure' },
              { type: 'Billing Records', period: '7 years', note: 'Required by tax law' },
              { type: 'Usage Logs', period: '12 months', note: 'Then anonymised and aggregated' },
            ].map(item => (
              <div key={item.type} className="flex flex-col sm:flex-row sm:items-center gap-2 py-3 border-b border-gray-100 last:border-0">
                <span className="text-sm font-semibold text-gray-800 sm:w-56 flex-shrink-0">{item.type}</span>
                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full sm:w-44 flex-shrink-0 text-center">{item.period}</span>
                <span className="text-xs text-gray-500">{item.note}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Incident response */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
            </svg>
            <div>
              <p className="font-bold text-amber-800 mb-1">Incident Response</p>
              <p className="text-sm text-amber-700">In the event of a data breach affecting your data, we will notify you within 72 hours of discovery as required by GDPR. Contact our security team at <a href="mailto:admin@uni5d.ai" className="font-semibold underline">admin@uni5d.ai</a></p>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="text-center text-sm text-gray-400">
          Security concerns or responsible disclosure: <a href="mailto:admin@uni5d.ai" className="text-blue-600 hover:underline">admin@uni5d.ai</a><br/>
          © 2026 Uni5d Technology Solutions LLC · Mooresville, North Carolina
        </div>
      </div>
    </div>
  );
}